import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Bell,
  CheckSquare,
  ClipboardList,
  Eye,
  GitBranch,
  Plus,
  Shield,
  Variable,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  ConfirmDialog,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui'
import { ProcessCanvas } from './ProcessCanvas.tsx'
import { StepInspector } from './StepInspector.tsx'
import { EdgeInspector } from './EdgeInspector.tsx'
import { VariablesManager } from './VariablesManager.tsx'
import { TemplatePreview } from './TemplatePreview.tsx'
import type {
  ConditionNode,
  GatewayType,
  ProcessTemplate,
  StepConfig,
  StepExecutor,
  StepType,
  TemplateStep,
  TemplateTransition,
  TemplateVariable,
} from './types.ts'

const STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  archived: 'Archiviert',
}

const STATUS_VARIANT: Record<string, string> = {
  draft: 'neutral',
  active: 'success',
  archived: 'warning',
}

interface StepPaletteItem {
  type: StepType
  label: string
  icon: React.ElementType
  defaultTitle: string
  colorClass: string
  /** Engine v2 executor this palette entry maps to. */
  executor: StepExecutor
  gatewayType?: GatewayType
}

const PALETTE: StepPaletteItem[] = [
  {
    type: 'task',
    label: 'Aufgabe',
    icon: CheckSquare,
    defaultTitle: 'Neue Aufgabe',
    colorClass: 'text-blue-700 border-blue-200 hover:bg-blue-50',
    executor: 'manual',
  },
  {
    type: 'approval',
    label: 'Freigabe-Gate',
    icon: Shield,
    defaultTitle: 'Freigabe erforderlich',
    colorClass: 'text-amber-700 border-amber-200 hover:bg-amber-50',
    executor: 'approval',
  },
  {
    type: 'form',
    label: 'Formular',
    icon: ClipboardList,
    defaultTitle: 'Neues Formular',
    colorClass: 'text-purple-700 border-purple-200 hover:bg-purple-50',
    executor: 'manual',
  },
  {
    type: 'notification',
    label: 'Benachrichtigung',
    icon: Bell,
    defaultTitle: 'Benachrichtigung',
    colorClass: 'text-teal-700 border-teal-200 hover:bg-teal-50',
    executor: 'manual',
  },
  {
    type: 'condition',
    label: 'Verzweigung (XOR)',
    icon: GitBranch,
    defaultTitle: 'Verzweigung',
    colorClass: 'text-orange-700 border-orange-200 hover:bg-orange-50',
    executor: 'gateway',
    gatewayType: 'xor',
  },
]

export function TemplateDesignerPage() {
  const { id: templateId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { toast } = useToast()

  const [steps, setSteps] = useState<TemplateStep[]>([])
  const [transitions, setTransitions] = useState<TemplateTransition[]>([])
  const [variables, setVariables] = useState<TemplateVariable[]>([])
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const [variablesOpen, setVariablesOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [savingInspector, setSavingInspector] = useState(false)
  const [savingEdge, setSavingEdge] = useState(false)
  const [publishing, setPublishing] = useState(false)

  // Always-fresh ref so async callbacks read current steps without stale closure issues
  const stepsRef = useRef<TemplateStep[]>(steps)
  useEffect(() => { stepsRef.current = steps }, [steps])

  const { data: template, isLoading, error } = useQuery<ProcessTemplate>({
    queryKey: ['prozesse-template', templateId],
    queryFn: () =>
      apiClient
        .get<ProcessTemplate>(`/prozesse/templates/${templateId}`)
        .then((r) => r.data),
    enabled: Boolean(templateId),
  })

  useEffect(() => {
    if (template?.steps) {
      setSteps([...template.steps].sort((a, b) => a.sort_order - b.sort_order))
    }
    if (template?.transitions) {
      setTransitions(template.transitions)
    }
    if (template?.variables) {
      setVariables(template.variables)
    }
  }, [template])

  const selectedStep = steps.find((s: TemplateStep) => s.id === selectedStepId) ?? null
  const selectedEdge = transitions.find((t) => t.id === selectedEdgeId) ?? null

  function selectStep(id: string) {
    setSelectedEdgeId(null)
    setSelectedStepId((prev: string | null) => (prev === id ? null : id))
  }

  function selectEdge(id: string) {
    setSelectedStepId(null)
    setSelectedEdgeId(id)
  }

  async function handleUpdateTransition(patch: {
    label?: string | null
    condition?: ConditionNode | null
    isDefault?: boolean
  }) {
    if (!selectedEdgeId) return
    setSavingEdge(true)
    setTransitions((prev) =>
      prev.map((t) =>
        t.id === selectedEdgeId
          ? {
              ...t,
              label: patch.label !== undefined ? patch.label : t.label,
              condition: patch.condition !== undefined ? patch.condition : t.condition,
              is_default: patch.isDefault !== undefined ? (patch.isDefault ? 1 : 0) : t.is_default,
            }
          : t,
      ),
    )
    try {
      await apiClient.patch(`/prozesse/templates/${templateId}/transitions/${selectedEdgeId}`, patch)
    } catch {
      toast({ title: 'Verbindung konnte nicht gespeichert werden', tone: 'danger' })
    } finally {
      setSavingEdge(false)
    }
  }

  async function handleAddStep(item: StepPaletteItem) {
    if (!templateId) return
    const { type, defaultTitle, executor, gatewayType } = item
    try {
      const { data } = await apiClient.post<{ id: string }>(
        `/prozesse/templates/${templateId}/steps`,
        {
          title: defaultTitle,
          stepType: type,
          isRequired: type !== 'notification',
          config: { enabled: true },
          executor,
          gatewayType: gatewayType ?? null,
        },
      )
      const newStep: TemplateStep = {
        id: data.id,
        template_id: templateId,
        title: defaultTitle,
        description: null,
        step_type: type,
        sort_order: (steps.length + 1) * 10,
        is_required: type !== 'notification' ? 1 : 0,
        config: { enabled: true },
        executor,
        gateway_type: gatewayType ?? null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      setSteps((prev: TemplateStep[]) => [...prev, newStep])
      setSelectedStepId(newStep.id)
    } catch {
      toast({ title: 'Schritt konnte nicht hinzugefügt werden', tone: 'danger' })
    }
  }

  async function handleToggleEnabled(stepId: string, enabled: boolean) {
    const current = stepsRef.current.find((s: TemplateStep) => s.id === stepId)
    const newConfig = { ...(current?.config ?? {}), enabled }
    setSteps((prev: TemplateStep[]) =>
      prev.map((s: TemplateStep) => (s.id === stepId ? { ...s, config: newConfig } : s)),
    )
    try {
      await apiClient.patch(`/prozesse/templates/${templateId}/steps/${stepId}`, {
        config: newConfig,
      })
    } catch {
      toast({ title: 'Status konnte nicht gespeichert werden', tone: 'danger' })
      if (template?.steps) setSteps([...template.steps].sort((a, b) => a.sort_order - b.sort_order))
    }
  }

  async function handleDeleteStep(stepId: string) {
    setConfirmDeleteId(null)
    if (selectedStepId === stepId) setSelectedStepId(null)
    const prev = steps
    const prevTransitions = transitions
    setSteps((s: TemplateStep[]) => s.filter((x: TemplateStep) => x.id !== stepId))
    // Backend cascade-deletes transitions touching this step; mirror locally.
    setTransitions((ts) => ts.filter((t) => t.from_step_id !== stepId && t.to_step_id !== stepId))
    try {
      await apiClient.delete(`/prozesse/templates/${templateId}/steps/${stepId}`)
    } catch {
      toast({ title: 'Schritt konnte nicht gelöscht werden', tone: 'danger' })
      setSteps(prev)
      setTransitions(prevTransitions)
    }
  }

  async function handleUpdateStep(
    patch: Partial<TemplateStep & { config: Partial<StepConfig> }>,
  ) {
    if (!selectedStepId) return
    setSavingInspector(true)
    const current = stepsRef.current.find((s: TemplateStep) => s.id === selectedStepId)
    const mergedConfig = patch.config
      ? { ...(current?.config ?? {}), ...patch.config }
      : current?.config ?? {}
    setSteps((prev: TemplateStep[]) =>
      prev.map((s: TemplateStep) =>
        s.id === selectedStepId ? { ...s, ...patch, config: mergedConfig } : s,
      ),
    )
    try {
      await apiClient.patch(`/prozesse/templates/${templateId}/steps/${selectedStepId}`, {
        title: patch.title,
        description: patch.description,
        stepType: patch.step_type,
        isRequired: patch.is_required !== undefined ? patch.is_required === 1 : undefined,
        config: mergedConfig,
        executor: patch.executor,
        gatewayType: patch.gateway_type,
        assignmentType: patch.assignment_type,
        assignmentValue: patch.assignment_value,
        actionKey: patch.action_key,
        subprocessTemplateId: patch.subprocess_template_id,
        activateAtRule: patch.activate_at_rule,
      })
    } catch {
      toast({ title: 'Änderungen konnten nicht gespeichert werden', tone: 'danger' })
      if (template?.steps) setSteps([...template.steps].sort((a, b) => a.sort_order - b.sort_order))
    } finally {
      setSavingInspector(false)
    }
  }

  async function handleNodeMove(stepId: string, position: { x: number; y: number }) {
    const current = stepsRef.current.find((s: TemplateStep) => s.id === stepId)
    const newConfig = { ...(current?.config ?? {}), position }
    setSteps((prev: TemplateStep[]) =>
      prev.map((s: TemplateStep) => (s.id === stepId ? { ...s, config: newConfig } : s)),
    )
    try {
      await apiClient.patch(`/prozesse/templates/${templateId}/steps/${stepId}`, {
        config: newConfig,
      })
    } catch {
      // Position persistence failure is non-critical; user can re-drag
    }
  }

  async function handleEdgeAdd(sourceId: string, targetId: string) {
    if (!templateId) return
    if (transitions.some((t) => t.from_step_id === sourceId && t.to_step_id === targetId)) return
    try {
      const { data } = await apiClient.post<{ id: string }>(
        `/prozesse/templates/${templateId}/transitions`,
        { fromStepId: sourceId, toStepId: targetId },
      )
      const created: TemplateTransition = {
        id: data.id,
        template_id: templateId,
        from_step_id: sourceId,
        to_step_id: targetId,
        condition: null,
        label: null,
        is_default: 0,
        sort_order: transitions.length,
      }
      setTransitions((prev) => [...prev, created])
    } catch {
      toast({ title: 'Verbindung konnte nicht gespeichert werden', tone: 'danger' })
    }
  }

  async function handleEdgeDelete(transitionId: string) {
    const prev = transitions
    setTransitions((ts) => ts.filter((t) => t.id !== transitionId))
    try {
      await apiClient.delete(`/prozesse/templates/${templateId}/transitions/${transitionId}`)
    } catch {
      toast({ title: 'Verbindung konnte nicht gelöscht werden', tone: 'danger' })
      setTransitions(prev)
    }
  }

  async function handlePublish() {
    if (!template) return
    const newStatus = template.status === 'active' ? 'draft' : 'active'
    setPublishing(true)
    try {
      await apiClient.patch(`/prozesse/templates/${templateId}`, { status: newStatus })
      qc.invalidateQueries({ queryKey: ['prozesse-template', templateId] })
      toast({
        title: newStatus === 'active' ? 'Vorlage aktiviert' : 'Vorlage auf Entwurf zurückgesetzt',
        tone: 'success',
      })
    } catch {
      toast({ title: 'Status konnte nicht geändert werden', tone: 'danger' })
    } finally {
      setPublishing(false)
    }
  }

  if (isLoading) return <LoadingState label="Vorlage wird geladen…" className="p-8" />
  if (error || !template) {
    return (
      <ErrorState
        title="Vorlage nicht gefunden"
        description="Die Vorlage konnte nicht geladen werden."
        details={error instanceof Error ? error.message : undefined}
        className="p-8"
      />
    )
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/prozesse/templates')}
            leadingIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Vorlagen
          </Button>
          <span className="text-gray-300">/</span>
          <h1 className="text-sm font-semibold text-gray-900 flex-1 truncate">{template.name}</h1>
          {template.description && (
            <span className="text-xs text-gray-500 truncate max-w-xs hidden lg:block">
              {template.description}
            </span>
          )}
          <Badge variant={STATUS_VARIANT[template.status] as 'neutral' | 'success' | 'warning'}>
            {STATUS_LABEL[template.status]}
          </Badge>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Variable className="w-4 h-4" />}
              onClick={() => setVariablesOpen(true)}
            >
              Variablen
              {variables.length > 0 && (
                <span className="ml-1 text-xs text-neutral-400">({variables.length})</span>
              )}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Eye className="w-4 h-4" />}
              onClick={() => setPreviewOpen(true)}
            >
              Vorschau
            </Button>
            {template.status !== 'archived' && (
              <Button
                variant={template.status === 'active' ? 'secondary' : 'primary'}
                size="sm"
                onClick={handlePublish}
                loading={publishing}
              >
                {template.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex flex-1 min-h-0">
        {/* Left palette */}
        <div className="w-56 bg-white border-r border-gray-200 p-3 shrink-0 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 px-1">
            Schritt hinzufügen
          </p>
          <div className="space-y-1.5">
            {PALETTE.map((item) => {
              const { type, label, icon: Icon, colorClass } = item
              return (
              <button
                key={type}
                type="button"
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border bg-white text-sm font-medium transition ${colorClass}`}
                onClick={() => handleAddStep(item)}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{label}</span>
                <Plus className="w-3.5 h-3.5 ml-auto opacity-60" />
              </button>
              )
            })}
          </div>

          <div className="border-t border-gray-200 mt-4 pt-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
              Tipp
            </p>
            <p className="text-xs text-gray-500 px-1 leading-relaxed">
              Ziehe Knoten frei. Verbinde Ausgänge (↓) mit Eingängen (↑) für Verzweigungen.{' '}
              <kbd className="bg-gray-100 px-1 rounded text-gray-600">Entf</kbd> löscht markierte Verbindungen.
            </p>
          </div>

          {steps.length > 0 && (
            <div className="border-t border-gray-200 mt-4 pt-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
                Übersicht
              </p>
              <div className="space-y-0.5">
                <div className="flex justify-between text-xs px-1 py-0.5 text-gray-600">
                  <span>Gesamt</span>
                  <span className="font-medium">{steps.length}</span>
                </div>
                <div className="flex justify-between text-xs px-1 py-0.5 text-gray-600">
                  <span>Aktiv</span>
                  <span className="font-medium text-green-600">
                    {steps.filter((s: TemplateStep) => s.config?.enabled !== false).length}
                  </span>
                </div>
                <div className="flex justify-between text-xs px-1 py-0.5 text-gray-600">
                  <span>Pflicht</span>
                  <span className="font-medium">
                    {steps.filter((s: TemplateStep) => s.is_required === 1).length}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Canvas — needs explicit height so React Flow fills the area */}
        <div className="flex-1 relative overflow-hidden">
          <ProcessCanvas
            steps={steps}
            transitions={transitions}
            selectedStepId={selectedStepId}
            selectedEdgeId={selectedEdgeId}
            onSelectStep={selectStep}
            onSelectEdge={selectEdge}
            onToggleEnabled={handleToggleEnabled}
            onDelete={(id) => setConfirmDeleteId(id)}
            onNodeMove={handleNodeMove}
            onEdgeAdd={handleEdgeAdd}
            onEdgeDelete={handleEdgeDelete}
          />
        </div>

        {/* Right inspector panel — step or edge */}
        {selectedStep && (
          <div className="w-80 bg-white border-l border-gray-200 shrink-0 flex flex-col overflow-hidden">
            <StepInspector
              step={selectedStep}
              variables={variables}
              onClose={() => setSelectedStepId(null)}
              onUpdate={handleUpdateStep}
              saving={savingInspector}
            />
          </div>
        )}
        {!selectedStep && selectedEdge && (
          <div className="w-80 bg-white border-l border-gray-200 shrink-0 flex flex-col overflow-hidden">
            <EdgeInspector
              transition={selectedEdge}
              variables={variables}
              fromTitle={steps.find((s) => s.id === selectedEdge.from_step_id)?.title}
              toTitle={steps.find((s) => s.id === selectedEdge.to_step_id)?.title}
              onUpdate={handleUpdateTransition}
              onDelete={() => { handleEdgeDelete(selectedEdge.id); setSelectedEdgeId(null) }}
              onClose={() => setSelectedEdgeId(null)}
              saving={savingEdge}
            />
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={Boolean(confirmDeleteId)}
        title="Schritt löschen?"
        message="Dieser Schritt wird unwiderruflich aus der Vorlage entfernt."
        confirmLabel="Löschen"
        variant="danger"
        onConfirm={() => { if (confirmDeleteId) handleDeleteStep(confirmDeleteId) }}
        onCancel={() => setConfirmDeleteId(null)}
      />

      {/* Variables manager */}
      {templateId && (
        <VariablesManager
          open={variablesOpen}
          onClose={() => setVariablesOpen(false)}
          templateId={templateId}
          variables={variables}
          onChange={setVariables}
        />
      )}

      {/* Preview modal */}
      <TemplatePreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        templateName={template.name}
        steps={steps}
      />
    </div>
  )
}
